import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, ClipboardList, MessageSquare, Users, Sparkles, Bell, LogOut, Plus, Minus, Trash2, Building2, CheckCircle2, ShieldCheck, ImagePlus, Images, Star, KeyRound, LayoutDashboard, CalendarClock, AlertTriangle } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";

const money = (n) => `$${Number(n).toFixed(2)}`;

function StatusPill({ q }) {
  const c = q <= 0 ? "bg-[#8B2E2E]" : q < 20 ? "bg-[#B56D22]" : "bg-[#2E4F3B]";
  return <span className={`text-white text-xs px-2 py-0.5 rounded-full ${c}`}>{q}</span>;
}

const uploadOne = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/admin/upload", fd).then((r) => r.data.url);
};

function PhotoManager({ images, setImages, testid = "photo" }) {
  const [busy, setBusy] = useState(false);
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadOne(f));
      setImages([...(images || []), ...urls]);
      toast.success(`${urls.length} photo(s) uploaded.`);
    } catch { toast.error("Upload failed. Use JPG/PNG/WEBP/GIF under 12MB."); }
    setBusy(false);
    e.target.value = "";
  };
  const remove = (i) => setImages(images.filter((_, x) => x !== i));
  const makeCover = (i) => { const c = [...images]; const [m] = c.splice(i, 1); setImages([m, ...c]); };
  return (
    <div className="space-y-2">
      <label className="flex items-center justify-center gap-2 border-2 border-dashed border-[#1A1514]/20 rounded-lg py-3 text-sm cursor-pointer hover:border-[#D4AF37] transition-colors" data-testid={`${testid}-upload`}>
        <ImagePlus className="w-4 h-4" /> {busy ? "Uploading…" : "Upload photos"}
        <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} disabled={busy} />
      </label>
      {(images || []).length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {images.map((g, i) => (
            <div key={i} className="relative group rounded-lg overflow-hidden border border-[#1A1514]/10" data-testid={`${testid}-thumb-${i}`}>
              <img src={mediaUrl(g)} alt="" className="w-full h-20 object-cover" />
              {i === 0 && <span className="absolute top-1 left-1 text-[9px] bg-[#D4AF37] text-[#1A1514] px-1 rounded font-medium">Cover</span>}
              <div className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-3 transition-opacity">
                {i !== 0 && <button type="button" onClick={() => makeCover(i)} title="Make cover" className="text-white hover:text-[#D4AF37]"><Star className="w-4 h-4" /></button>}
                <button type="button" onClick={() => remove(i)} title="Remove" className="text-white hover:text-[#8B2E2E]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PhotosDialog({ item, onClose, onSaved }) {
  const [images, setImages] = useState([]);
  useEffect(() => {
    if (item) setImages(item.images && item.images.length ? item.images : (item.image ? [item.image] : []));
  }, [item]);
  if (!item) return null;
  const save = async () => {
    await api.put(`/admin/items/${item.id}`, { images, image: images[0] || "" });
    toast.success("Gallery saved.");
    onSaved();
  };
  return (
    <Dialog open={!!item} onOpenChange={onClose}>
      <DialogContent className="bg-[#F9F8F6]">
        <DialogHeader><DialogTitle className="font-heading text-2xl font-light">Photos — {item.name}</DialogTitle>
          <DialogDescription>Upload at least 4 photos. The first photo (Cover) is shown on the product card.</DialogDescription></DialogHeader>
        <PhotoManager images={images} setImages={setImages} testid="edit-photo" />
        <Button onClick={save} className="w-full rounded-full bg-[#1A1514] text-[#F9F8F6] mt-2" data-testid="save-photos-btn">Save Gallery</Button>
      </DialogContent>
    </Dialog>
  );
}

function Inventory() {
  const [items, setItems] = useState([]);
  const [photosItem, setPhotosItem] = useState(null);
  const [form, setForm] = useState({ name: "", category: "", price: "", total_quantity: "", replacement_cost: "", unit: "unit", image: "", images: [], description: "" });
  const load = () => api.get("/admin/items").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const adjust = async (id, delta) => { await api.post(`/admin/items/${id}/adjust?delta=${delta}`); load(); };
  const create = async () => {
    if (!form.name || !form.price || !form.total_quantity) return toast.error("Name, price and quantity required.");
    await api.post("/admin/items", { ...form, price: parseFloat(form.price), total_quantity: parseInt(form.total_quantity), replacement_cost: parseFloat(form.replacement_cost) || 0 });
    toast.success("Item added."); setForm({ name: "", category: "", price: "", total_quantity: "", replacement_cost: "", unit: "unit", image: "", images: [], description: "" }); load();
  };
  const remove = async (id) => { await api.delete(`/admin/items/${id}`); toast.success("Item removed."); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-3xl font-light">Inventory</h2>
        <Dialog>
          <DialogTrigger asChild><Button className="rounded-full bg-[#1A1514] text-[#F9F8F6]" data-testid="add-item-btn"><Plus className="w-4 h-4 mr-2" />Add Item</Button></DialogTrigger>
          <DialogContent className="bg-[#F9F8F6]">
            <DialogHeader><DialogTitle className="font-heading text-2xl font-light">New Rental Item</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-item-name" />
              <Input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="new-item-category" />
              <div className="grid grid-cols-2 gap-3">
                <Input type="number" placeholder="Rental price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="new-item-price" />
                <Input type="number" placeholder="Replacement cost (damage/loss)" value={form.replacement_cost} onChange={(e) => setForm({ ...form, replacement_cost: e.target.value })} data-testid="new-item-replacement" />
                <Input type="number" placeholder="Quantity" value={form.total_quantity} onChange={(e) => setForm({ ...form, total_quantity: e.target.value })} data-testid="new-item-qty" />
                <Input placeholder="Unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
              </div>
              <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <div><Label className="text-xs mb-1 block">Photos (upload 4+, first is the cover)</Label>
                <PhotoManager images={form.images} setImages={(imgs) => setForm({ ...form, images: imgs })} testid="new-photo" /></div>
              <Button onClick={create} className="w-full rounded-full bg-[#1A1514] text-[#F9F8F6]" data-testid="save-item-btn">Save Item</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white rounded-xl border border-[#1A1514]/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="text-left text-[#1A1514]/50 text-xs uppercase tracking-wider border-b border-[#1A1514]/10">
            <th className="p-4">Item</th><th className="p-4">Price</th><th className="p-4">Available</th><th className="p-4">Total</th><th className="p-4 text-right">Adjust stock</th></tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className={`border-b border-[#1A1514]/5 ${!it.active && "opacity-40"}`} data-testid={`inv-row-${it.id}`}>
                <td className="p-4"><div className="flex items-center gap-3">
                  {it.image && <img src={mediaUrl(it.image)} alt="" className="w-10 h-10 rounded object-cover" />}
                  <div><p className="font-medium">{it.name}</p><p className="text-xs text-[#1A1514]/40">{it.category} · {(it.images || []).length || (it.image ? 1 : 0)} photo(s)</p></div></div></td>
                <td className="p-4 font-mono">{money(it.price)}</td>
                <td className="p-4"><StatusPill q={it.available_quantity} /></td>
                <td className="p-4 font-mono">{it.total_quantity}</td>
                <td className="p-4"><div className="flex items-center justify-end gap-2">
                  <button className="w-7 h-7 rounded-full border border-[#1A1514]/15 flex items-center justify-center" onClick={() => adjust(it.id, -10)} data-testid={`dec-${it.id}`}><Minus className="w-3 h-3" /></button>
                  <button className="w-7 h-7 rounded-full border border-[#1A1514]/15 flex items-center justify-center" onClick={() => adjust(it.id, 10)} data-testid={`inc-${it.id}`}><Plus className="w-3 h-3" /></button>
                  <button className="ml-2" onClick={() => setPhotosItem(it)} data-testid={`photos-${it.id}`} title="Manage photos"><Images className="w-4 h-4 text-[#1A1514]/60" /></button>
                  <button className="ml-1" onClick={() => remove(it.id)} data-testid={`del-${it.id}`}><Trash2 className="w-4 h-4 text-[#8B2E2E]" /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PhotosDialog item={photosItem} onClose={() => setPhotosItem(null)} onSaved={() => { setPhotosItem(null); load(); }} />
    </div>
  );
}

function DamageDialog({ order, onClose, onDone }) {
  const [qty, setQty] = useState({});
  const [note, setNote] = useState("");
  if (!order) return null;
  const charge = async () => {
    const items = (order.lines || []).filter((l) => (qty[l.item_id] || 0) > 0)
      .map((l) => ({ item_id: l.item_id, quantity: parseInt(qty[l.item_id]) }));
    if (items.length === 0) return toast.error("Enter a quantity for at least one item.");
    try {
      const r = await api.post(`/admin/orders/${order.id}/charge-damage`, { items, note });
      toast.success(`Damage charge $${r.data.amount.toFixed(2)} ${r.data.paid ? "charged to card." : "recorded."}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Charge failed."); }
  };
  return (
    <Dialog open={!!order} onOpenChange={onClose}>
      <DialogContent className="bg-[#F9F8F6]">
        <DialogHeader><DialogTitle className="font-heading text-2xl font-light">Report damage / loss</DialogTitle>
          <DialogDescription>Enter the quantity broken or lost per item. The replacement cost is charged to the saved card when available.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {(order.lines || []).map((l) => (
            <div key={l.item_id} className="flex items-center justify-between gap-3">
              <span className="text-sm">{l.name} <span className="text-xs text-[#1A1514]/40">(rented {l.quantity})</span></span>
              <Input type="number" min="0" max={l.quantity} className="w-24" placeholder="0"
                value={qty[l.item_id] || ""} onChange={(e) => setQty({ ...qty, [l.item_id]: e.target.value })}
                data-testid={`dmg-qty-${l.item_id}`} />
            </div>
          ))}
          <Textarea placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="dmg-note" />
          <Button onClick={charge} className="w-full rounded-full bg-[#8B2E2E] text-white" data-testid="dmg-charge-btn">Charge replacement cost</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Reservations() {
  const [orders, setOrders] = useState([]);
  const [dmg, setDmg] = useState(null);
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);
  const markPaid = async (id) => { await api.post(`/admin/orders/${id}/mark-paid`); toast.success("Marked paid — inventory updated."); load(); };
  return (
    <div>
      <h2 className="font-heading text-3xl font-light mb-6">Reservations</h2>
      <div className="space-y-4">
        {orders.length === 0 && <p className="text-[#1A1514]/50 text-sm">No reservations yet.</p>}
        {orders.map((o) => (
          <div key={o.id} className="bg-white rounded-xl border border-[#1A1514]/10 p-5" data-testid={`order-${o.id}`}>
            <div className="flex justify-between flex-wrap gap-2">
              <div>
                <p className="font-medium">{o.customer_name} · <span className="font-mono text-sm">{money(o.total)}</span> <span className="text-xs text-[#1A1514]/40">via {o.method}</span></p>
                <p className="text-xs text-[#1A1514]/50">{o.customer_email} · {o.customer_phone} · Event {o.event_date}</p>
              </div>
              <div className="flex items-center gap-2 h-fit">
                <span className={`text-xs px-3 py-1 rounded-full ${o.payment_status === "paid" ? "bg-[#2E4F3B] text-white" : "bg-[#B56D22] text-white"}`}>{o.payment_status}</span>
                {o.payment_status !== "paid" && <button onClick={() => markPaid(o.id)} data-testid={`mark-paid-${o.id}`}
                  className="text-xs flex items-center gap-1 px-3 py-1 rounded-full bg-[#1A1514] text-white"><CheckCircle2 className="w-3 h-3" /> Mark paid</button>}
                {o.payment_status === "paid" && <button onClick={() => setDmg(o)} data-testid={`report-damage-${o.id}`}
                  className="text-xs px-3 py-1 rounded-full bg-[#8B2E2E] text-white">Report damage/loss</button>}
              </div>
            </div>
            <p className="text-sm text-[#1A1514]/70 mt-3">{o.lines.map((l) => `${l.quantity}× ${l.name}`).join(", ")}</p>
            {o.notes && <p className="text-xs text-[#1A1514]/50 mt-1">Note: {o.notes}</p>}
            {(o.fulfillment?.delivery || o.fulfillment?.pickup) &&
              <p className="text-xs text-[#1A1514]/50 mt-1">{o.fulfillment.delivery && "Delivery"} {o.fulfillment.pickup && "Pickup"} · {o.fulfillment.miles} mi · fee {money(o.fee)}</p>}
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.id}/receipt?token=${o.receipt_token}`} target="_blank" rel="noreferrer"
              className="inline-block mt-2 text-xs text-[#D4AF37] underline" data-testid={`receipt-${o.id}`}>Download receipt (PDF)</a>
            {o.return_required && o.payment_status === "paid" && (
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs" data-testid={`return-row-${o.id}`}>
                <span className={o.returned ? "text-[#2E4F3B]" : "text-[#B56D22]"}>
                  {o.returned ? "Returned ✓" : `Return due ${o.return_due ? new Date(o.return_due).toLocaleString() : "—"}`}
                </span>
                {!o.returned && <button onClick={async () => { await api.post(`/admin/orders/${o.id}/mark-returned`); toast.success("Marked returned."); load(); }}
                  className="px-3 py-1 rounded-full bg-[#1A1514] text-white" data-testid={`mark-returned-${o.id}`}>Mark returned</button>}
                {o.late_charged && <span className="text-[#8B2E2E]">Late day ${(o.late_charge_amount || 0).toFixed(2)} {o.late_charge_paid ? "charged" : "due"}</span>}
              </div>
            )}
          </div>
        ))}
      </div>
      <DamageDialog order={dmg} onClose={() => setDmg(null)} onDone={() => { setDmg(null); load(); }} />
    </div>
  );
}

const SITE_FIELDS = [
  { key: "business_name", label: "Business Name" },
  { key: "tagline", label: "Tagline" },
  { key: "address", label: "Address" },
  { key: "phone", label: "Phone" },
  { key: "phone2", label: "Second Phone" },
  { key: "email", label: "Email" },
  { key: "zelle_name", label: "Zelle Name" },
  { key: "zelle_number", label: "Zelle Number" },
  { key: "hours", label: "Hours" },
  { key: "logo_url", label: "Logo Image URL" },
  { key: "letterhead_url", label: "Letterhead Image URL" },
];

function BusinessInfo() {
  const [site, setSite] = useState(null);
  useEffect(() => { api.get("/admin/site").then((r) => setSite({ custom_info: [], ...r.data })); }, []);
  if (!site) return null;
  const set = (k, v) => setSite({ ...site, [k]: v });
  const setCustom = (i, k, v) => { const c = [...site.custom_info]; c[i] = { ...c[i], [k]: v }; set("custom_info", c); };
  const addCustom = () => set("custom_info", [...site.custom_info, { label: "", value: "" }]);
  const delCustom = (i) => set("custom_info", site.custom_info.filter((_, x) => x !== i));
  const save = async () => {
    const payload = { ...site };
    payload.custom_info = (site.custom_info || []).filter((c) => c.label);
    await api.put("/admin/site", payload);
    toast.success("Business info updated.");
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-3xl font-light">Business Info</h2>
        <Button onClick={save} className="rounded-full bg-[#1A1514] text-[#F9F8F6]" data-testid="save-site-btn">Save Changes</Button>
      </div>
      {site.letterhead_url && <img src={site.letterhead_url} alt="Letterhead" className="w-full max-h-52 object-contain rounded-xl border border-[#1A1514]/10 bg-white mb-6" />}
      <div className="bg-white rounded-xl border border-[#1A1514]/10 p-6 grid md:grid-cols-2 gap-4">
        {SITE_FIELDS.map((f) => (
          <div key={f.key}><Label className="text-xs">{f.label}</Label>
            <Input value={site[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`site-${f.key}`} /></div>
        ))}
        <div className="md:col-span-2"><Label className="text-xs">About</Label>
          <Textarea value={site.about || ""} onChange={(e) => set("about", e.target.value)} data-testid="site-about" /></div>
        <div className="md:col-span-2"><Label className="text-xs">Rental Agreement / Terms (shown at checkout & in receipts)</Label>
          <Textarea rows={10} value={site.terms || ""} onChange={(e) => set("terms", e.target.value)} data-testid="site-terms" /></div>
        <div className="md:col-span-2"><Label className="text-xs">Cancellation Policy</Label>
          <Textarea value={site.cancellation_policy || ""} onChange={(e) => set("cancellation_policy", e.target.value)} data-testid="site-cancellation" /></div>
      </div>
      <div className="bg-white rounded-xl border border-[#1A1514]/10 p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm uppercase tracking-[0.2em] text-[#1A1514]/50">Additional Info</p>
          <Button onClick={addCustom} variant="outline" className="rounded-full" data-testid="add-custom-info"><Plus className="w-4 h-4 mr-1" />Add field</Button>
        </div>
        <div className="space-y-3">
          {(site.custom_info || []).length === 0 && <p className="text-sm text-[#1A1514]/40">Add any extra info you want shown on the site (policies, hours, social links, etc.)</p>}
          {(site.custom_info || []).map((c, i) => (
            <div key={i} className="flex gap-3 items-center">
              <Input placeholder="Label" value={c.label} onChange={(e) => setCustom(i, "label", e.target.value)} data-testid={`custom-label-${i}`} />
              <Input placeholder="Value" value={c.value} onChange={(e) => setCustom(i, "value", e.target.value)} data-testid={`custom-value-${i}`} />
              <button onClick={() => delCustom(i)}><Trash2 className="w-4 h-4 text-[#8B2E2E]" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Inquiries() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/inquiries").then((r) => setRows(r.data)); }, []);
  return (
    <div>
      <h2 className="font-heading text-3xl font-light mb-6">Inquiries</h2>
      <div className="space-y-3">
        {rows.length === 0 && <p className="text-[#1A1514]/50 text-sm">No inquiries yet.</p>}
        {rows.map((q) => (
          <div key={q.id} className="bg-white rounded-xl border border-[#1A1514]/10 p-5" data-testid={`inquiry-${q.id}`}>
            <p className="font-medium">{q.name} <span className="text-xs text-[#1A1514]/50">{q.email} · {q.phone}</span></p>
            <p className="text-sm text-[#1A1514]/70 mt-2">{q.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CrudList({ title, endpoint, fields, testid }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({});
  const load = () => api.get(`/admin/${endpoint}`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    const payload = {};
    fields.forEach((f) => { payload[f.key] = f.type === "number" ? parseFloat(form[f.key] || 0) : (form[f.key] || ""); });
    if (!payload[fields[0].key]) return toast.error(`${fields[0].label} required.`);
    await api.post(`/admin/${endpoint}`, payload); setForm({}); load(); toast.success("Added.");
  };
  const del = async (id) => { await api.delete(`/admin/${endpoint}/${id}`); load(); };
  return (
    <div>
      <h2 className="font-heading text-3xl font-light mb-6">{title}</h2>
      <div className="bg-white rounded-xl border border-[#1A1514]/10 p-5 mb-6 grid md:grid-cols-4 gap-3 items-end">
        {fields.map((f) => (
          <div key={f.key}><Label className="text-xs">{f.label}</Label>
            <Input type={f.type || "text"} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testid}-${f.key}`} /></div>
        ))}
        <Button onClick={add} className="rounded-full bg-[#1A1514] text-[#F9F8F6]" data-testid={`${testid}-add`}>Add</Button>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="bg-white rounded-xl border border-[#1A1514]/10 p-4 flex justify-between items-center" data-testid={`${testid}-row-${r.id}`}>
            <div className="text-sm">{fields.map((f) => <span key={f.key} className="mr-4">{f.type === "number" ? money(r[f.key]) : r[f.key]}</span>)}</div>
            <button onClick={() => del(r.id)}><Trash2 className="w-4 h-4 text-[#8B2E2E]" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Notifications() {
  const [settings, setSettings] = useState({ notify_emails: [], notify_phones: [] });
  const [logs, setLogs] = useState([]);
  const [emails, setEmails] = useState("");
  const [phones, setPhones] = useState("");
  useEffect(() => {
    api.get("/admin/settings").then((r) => { setSettings(r.data); setEmails((r.data.notify_emails || []).join(", ")); setPhones((r.data.notify_phones || []).join(", ")); });
    api.get("/admin/notifications").then((r) => setLogs(r.data));
  }, []);
  const save = async () => {
    await api.put("/admin/settings", {
      notify_emails: emails.split(",").map((s) => s.trim()).filter(Boolean),
      notify_phones: phones.split(",").map((s) => s.trim()).filter(Boolean),
    });
    toast.success("Notification recipients saved.");
  };
  return (
    <div>
      <h2 className="font-heading text-3xl font-light mb-6">Notifications</h2>
      <div className="bg-white rounded-xl border border-[#1A1514]/10 p-6 space-y-4 mb-8">
        <p className="text-sm text-[#1A1514]/60">Confirmations & inquiries are sent to these recipients (comma-separated).</p>
        <div><Label className="text-xs">Notify emails</Label>
          <Input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="owner@site.com, manager@site.com" data-testid="notify-emails" /></div>
        <div><Label className="text-xs">Notify phone numbers</Label>
          <Input value={phones} onChange={(e) => setPhones(e.target.value)} placeholder="+14105551234, +14105555678" data-testid="notify-phones" /></div>
        <Button onClick={save} className="rounded-full bg-[#1A1514] text-[#F9F8F6]" data-testid="save-notify">Save Recipients</Button>
      </div>
      <h3 className="font-heading text-xl mb-3">Sent Log</h3>
      <div className="space-y-2">
        {logs.length === 0 && <p className="text-[#1A1514]/50 text-sm">No notifications sent yet.</p>}
        {logs.map((l) => (
          <div key={l.id} className="bg-white rounded-xl border border-[#1A1514]/10 p-4 text-sm">
            <p className="font-medium">{l.subject}</p>
            <p className="text-[#1A1514]/60 text-xs mt-1">{l.body}</p>
            <p className="text-[11px] text-[#1A1514]/40 mt-1">Email {l.email_sent ? "✓" : "queued"} · SMS {l.sms_sent ? "✓" : "queued"}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function TeamAccess() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", role: "staff", password: "" });
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.email) return toast.error("Email is required.");
    try {
      await api.post("/admin/users", form);
      toast.success("Team member added.");
      setForm({ name: "", email: "", role: "staff", password: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add team member."); }
  };
  const del = async (email) => {
    try { await api.delete(`/admin/users/${encodeURIComponent(email)}`); toast.success("Team member removed."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove."); }
  };
  return (
    <div>
      <h2 className="font-heading text-3xl font-light mb-2">Team Access</h2>
      <p className="text-sm text-[#1A1514]/50 mb-6">People who can sign in to this dashboard. Leave the password blank to allow Google sign-in only.</p>
      <div className="bg-white rounded-xl border border-[#1A1514]/10 p-5 mb-6 grid md:grid-cols-5 gap-3 items-end">
        <div><Label className="text-xs">Name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name" /></div>
        <div><Label className="text-xs">Email</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-email" /></div>
        <div><Label className="text-xs">Role</Label>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="team-role"
            className="w-full h-10 rounded-md border border-[#1A1514]/15 bg-white px-3 text-sm">
            <option value="staff">Staff</option>
            <option value="owner">Owner</option>
          </select></div>
        <div><Label className="text-xs">Password (optional)</Label>
          <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="team-password" /></div>
        <Button onClick={add} className="rounded-full bg-[#1A1514] text-[#F9F8F6]" data-testid="team-add">Add</Button>
      </div>
      <div className="space-y-2">
        {users.map((u) => (
          <div key={u.email} className="bg-white rounded-xl border border-[#1A1514]/10 p-4 flex justify-between items-center" data-testid={`team-row-${u.email}`}>
            <div className="text-sm">
              <span className="font-medium mr-3">{u.name || "—"}</span>
              <span className="text-[#1A1514]/60 mr-3">{u.email}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${u.role === "owner" ? "bg-[#D4AF37] text-[#1A1514]" : "bg-[#1A1514]/10 text-[#1A1514]"}`}>{u.role}</span>
              <span className="text-xs text-[#1A1514]/40 ml-3">{u.has_password ? "password + Google" : "Google only"}</span>
            </div>
            {u.role !== "owner" && <button onClick={() => del(u.email)} data-testid={`team-del-${u.email}`}><Trash2 className="w-4 h-4 text-[#8B2E2E]" /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Overview() {
  const [s, setS] = useState(null);
  const load = () => api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const clearDemo = async () => {
    if (!window.confirm("Remove all demo/test orders, inquiries and notifications? This cannot be undone.")) return;
    try {
      const r = await api.post("/admin/cleanup-demo-data");
      toast.success(`Cleared ${r.data.orders} test orders, ${r.data.inquiries} inquiries.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not clear demo data."); }
  };
  if (!s) return <p className="text-sm text-[#1A1514]/50" data-testid="overview-loading">Loading…</p>;
  const cards = [
    { label: "Revenue · last 7 days", value: `$${s.revenue_week.toFixed(2)}`, sub: `${s.week_count} paid reservation(s)` },
    { label: "Total revenue", value: `$${s.revenue_total.toFixed(2)}`, sub: `${s.paid_count} paid all-time` },
    { label: "Awaiting payment", value: s.pending_count, sub: "pending / Zelle / PayPal" },
    { label: "Low-stock items", value: s.low_stock.length, sub: "below 20 available" },
  ];
  return (
    <div data-testid="overview-panel">
      <h2 className="font-heading text-3xl font-light mb-2">Overview</h2>
      <div className="flex items-center justify-between mb-6 gap-3">
        <p className="text-sm text-[#1A1514]/50">A quick pulse of your rental business.</p>
        <button onClick={clearDemo} data-testid="clear-demo-btn" className="shrink-0 text-xs px-3 py-1.5 rounded-full border border-[#8B2E2E]/30 text-[#8B2E2E] hover:bg-[#8B2E2E]/5 transition-colors">Clear demo data</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-[#1A1514]/10 rounded-xl p-5" data-testid={`stat-${c.label}`}>
            <p className="text-xs uppercase tracking-[0.15em] text-[#1A1514]/50">{c.label}</p>
            <p className="font-heading text-3xl mt-2">{c.value}</p>
            <p className="text-xs text-[#1A1514]/40 mt-1">{c.sub}</p>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#1A1514]/10 rounded-xl p-5">
          <h3 className="flex items-center gap-2 font-medium mb-3"><CalendarClock className="w-4 h-4 text-[#D4AF37]" /> Upcoming events</h3>
          {s.upcoming.length === 0 ? <p className="text-sm text-[#1A1514]/40">No upcoming paid reservations.</p> : (
            <div className="space-y-2">
              {s.upcoming.map((o) => (
                <div key={o.id} className="flex justify-between items-center text-sm border-b border-[#1A1514]/5 pb-2 last:border-0" data-testid={`upcoming-${o.id}`}>
                  <div><span className="font-medium">{o.customer_name}</span><span className="text-[#1A1514]/50 ml-2">{o.event_date}</span></div>
                  <span className="font-mono">${(o.total || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-[#1A1514]/10 rounded-xl p-5">
          <h3 className="flex items-center gap-2 font-medium mb-3"><AlertTriangle className="w-4 h-4 text-[#B56D22]" /> Low-stock alerts</h3>
          {s.low_stock.length === 0 ? <p className="text-sm text-[#1A1514]/40">All items well stocked.</p> : (
            <div className="space-y-2">
              {s.low_stock.map((i) => (
                <div key={i.name} className="flex justify-between items-center text-sm border-b border-[#1A1514]/5 pb-2 last:border-0">
                  <span>{i.name}</span><StatusPill q={i.available_quantity} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "inventory", label: "Inventory", icon: Package },
  { key: "reservations", label: "Reservations", icon: ClipboardList },
  { key: "inquiries", label: "Inquiries", icon: MessageSquare },
  { key: "staff", label: "Staff", icon: Users },
  { key: "team", label: "Team Access", icon: ShieldCheck },
  { key: "services", label: "Services", icon: Sparkles },
  { key: "business", label: "Business Info", icon: Building2 },
  { key: "notifications", label: "Notifications", icon: Bell },
];

function ChangePasswordDialog({ open, setOpen }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (nw.length < 8) return toast.error("New password must be at least 8 characters.");
    if (nw !== cf) return toast.error("New passwords do not match.");
    setBusy(true);
    try {
      await api.post("/admin/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated.");
      setOpen(false); setCur(""); setNw(""); setCf("");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update password."); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#F9F8F6] max-w-sm">
        <DialogHeader><DialogTitle className="font-heading text-2xl font-light">Change password</DialogTitle>
          <DialogDescription>Update the password for your admin account.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Current password</Label>
            <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cur-password" /></div>
          <div><Label className="text-xs">New password</Label>
            <Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="new-password" /></div>
          <div><Label className="text-xs">Confirm new password</Label>
            <Input type="password" value={cf} onChange={(e) => setCf(e.target.value)} data-testid="confirm-password" /></div>
          <Button onClick={submit} disabled={busy} className="w-full rounded-full bg-[#1A1514] text-[#F9F8F6]" data-testid="save-password-btn">{busy ? "Saving…" : "Update Password"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [role, setRole] = useState("");
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) { nav("/admin/login"); return; }
    api.get("/admin/me").then((r) => setRole(r.data.role))
      .catch(() => { localStorage.removeItem("admin_token"); nav("/admin/login"); });
  }, []); // eslint-disable-line

  const logout = async () => {
    try { await api.post("/admin/logout"); } catch { /* ignore */ }
    localStorage.removeItem("admin_token");
    nav("/admin/login");
  };

  const visibleTabs = TABS.filter((t) => t.key !== "team" || role === "owner");

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#F9F8F6]">
      <aside className="w-full md:w-60 bg-[#1A1514] text-[#F9F8F6] p-5 md:p-6 flex flex-col">
        <p className="font-heading text-2xl mb-6 md:mb-10">Pascy <span className="text-[#D4AF37]">Lux</span></p>
        <nav className="flex md:flex-col gap-1 flex-1 overflow-x-auto md:overflow-visible">
          {visibleTabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`shrink-0 w-auto md:w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors whitespace-nowrap ${tab === t.key ? "bg-white/10 text-[#D4AF37]" : "text-white/60 hover:bg-white/5"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
        <button onClick={() => setShowPwd(true)} data-testid="change-password-btn" className="hidden md:flex items-center gap-3 px-4 py-3 text-sm text-white/60 hover:text-white">
          <KeyRound className="w-4 h-4" /> Change Password
        </button>
        <button onClick={logout} data-testid="logout-btn" className="hidden md:flex items-center gap-3 px-4 py-3 text-sm text-white/60 hover:text-white">
          <LogOut className="w-4 h-4" /> Sign Out
        </button>
      </aside>
      <main className="flex-1 p-5 md:p-10 overflow-y-auto">
        {tab === "overview" && <Overview />}
        {tab === "inventory" && <Inventory />}
        {tab === "reservations" && <Reservations />}
        {tab === "inquiries" && <Inquiries />}
        {tab === "staff" && <CrudList title="Staff" endpoint="staff" testid="staff"
          fields={[{ key: "name", label: "Name" }, { key: "role", label: "Role" }, { key: "phone", label: "Phone" }, { key: "email", label: "Email" }]} />}
        {tab === "team" && role === "owner" && <TeamAccess />}
        {tab === "services" && <CrudList title="Services" endpoint="services" testid="service"
          fields={[{ key: "name", label: "Name" }, { key: "price", label: "Price", type: "number" }, { key: "description", label: "Description" }]} />}
        {tab === "business" && <BusinessInfo />}
        {tab === "notifications" && <Notifications />}
      </main>
      <ChangePasswordDialog open={showPwd} setOpen={setShowPwd} />
    </div>
  );
}
